// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/tracking/Tracking.tsx
// 
// تغییرات این نسخه:
// - 🆕 نمایش notes (توضیحات) در لیست "آخرین بارها" در داشبورد
// - 🆕 نمایش invoice_number در داشبورد
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { trackingAPI } from "../../services/api";
import {
  LayoutDashboard, Truck, AlertTriangle, Settings,
  Package, Clock, CheckCircle2, ArrowRight,
} from "lucide-react";
import ShipmentsList from "./ShipmentsList";
import ShortageReport from "./ShortageReport";
import OfficesManager from "./OfficesManager";

type Tab = "dashboard" | "shipments" | "shortage" | "offices";

const fmt = (n: any) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(Number(n) || 0);

const STATUS_LABELS: Record<string, string> = {
  in_customs: "در گمرک",
  clearing:   "در حال ترخیص",
  cleared:    "ترخیص شده",
  distributed:"توزیع شده",
};

const STATUS_COLORS: Record<string, string> = {
  in_customs: "#f59e0b",
  clearing:   "#3b82f6",
  cleared:    "#06b6d4",
  distributed:"#10b981",
};

export default function Tracking() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const [tab, setTab] = useState<Tab>("dashboard");
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const r = await trackingAPI.dashboard();
      if (r.success) setStats(r.stats);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    if (tab === "dashboard") loadStats();
  }, [tab, loadStats]);

  const tabs = [
    { id: "dashboard" as Tab, label: "داشبورد",      icon: LayoutDashboard, color: "#f97316" },
    { id: "shipments" as Tab, label: "بارها",         icon: Truck,           color: "#3b82f6" },
    { id: "shortage"  as Tab, label: "گزارش کسری",   icon: AlertTriangle,   color: "#ef4444" },
    { id: "offices"   as Tab, label: "گمرک‌ها",        icon: Settings,        color: "#8b5cf6" },
  ];

  return (
    <div style={{ padding: "28px 28px 60px", fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }} dir="rtl">
      
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 11, color: "#f97316", fontWeight: 700, letterSpacing: "1.5px", marginBottom: 6 }}>
          🚛 پیگیری اجناس
        </div>
        <h1 style={{ fontSize: 26, fontWeight: 900, margin: 0, color: T.text }}>پیگیری بارهای گمرکی</h1>
        <p style={{ fontSize: 13, color: T.subtext, margin: "5px 0 0" }}>ثبت بارها، توزیع به گدام/مشتری و پیگیری کسری</p>
      </div>

      {/* Tabs */}
      <div style={{
        display: "flex", gap: 8, marginBottom: 28, padding: 6,
        background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)",
        border: `1px solid ${T.cardBorder}`, borderRadius: 14,
      }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
            gap: 8, padding: "11px 16px", borderRadius: 10, border: "none",
            cursor: "pointer", fontSize: 13, fontWeight: 700,
            fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
            background: tab === t.id ? `linear-gradient(135deg,${t.color},${t.color}bb)` : "transparent",
            color: tab === t.id ? "#fff" : T.subtext,
            transition: "all 0.2s",
            boxShadow: tab === t.id ? `0 4px 14px ${t.color}40` : "none",
          }}>
            <t.icon size={16} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Dashboard */}
      {tab === "dashboard" && (
        loading ? (
          <div style={{ textAlign: "center", padding: 80, color: T.subtext }}>
            <div style={{ width: 40, height: 40, border: "3px solid rgba(249,115,22,0.2)", borderTop: "3px solid #f97316", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
            در حال بارگذاری...
          </div>
        ) : (
          <div>
            {/* کارت‌های آمار وضعیت */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14, marginBottom: 28 }}>
              {[
                { label: "در گمرک",       val: stats?.status_counts?.in_customs ?? 0, color: STATUS_COLORS.in_customs, icon: Clock },
                { label: "در حال ترخیص",  val: stats?.status_counts?.clearing ?? 0,   color: STATUS_COLORS.clearing,   icon: Package },
                { label: "ترخیص شده",     val: stats?.status_counts?.cleared ?? 0,    color: STATUS_COLORS.cleared,    icon: ArrowRight },
                { label: "توزیع شده",     val: stats?.status_counts?.distributed ?? 0,color: STATUS_COLORS.distributed,icon: CheckCircle2 },
                { label: "بیش از 15 روز در گمرک", val: stats?.long_in_customs ?? 0,   color: "#dc2626", icon: AlertTriangle },
                { label: "تعداد کسری‌ها",  val: stats?.shortage_count ?? 0,             color: "#f97316", icon: AlertTriangle },
              ].map((card, i) => (
                <div key={i} style={{
                  borderRadius: 16, padding: 20,
                  background: dark ? "rgba(255,255,255,0.03)" : "#fff",
                  border: `1px solid ${T.cardBorder}`,
                  borderTop: `3px solid ${card.color}`,
                  transition: "transform 0.2s",
                }}
                  onMouseEnter={e => (e.currentTarget.style.transform = "translateY(-2px)")}
                  onMouseLeave={e => (e.currentTarget.style.transform = "translateY(0)")}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: `${card.color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <card.icon size={17} color={card.color} />
                    </div>
                    <div style={{ fontSize: 11, color: T.subtext, fontWeight: 600 }}>{card.label}</div>
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: card.color }}>{fmt(card.val)}</div>
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              {/* بارهای اخیر */}
              <div style={{ borderRadius: 16, padding: 20, background: dark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${T.cardBorder}` }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 16 }}>📋 آخرین بارها</div>
                {(stats?.recent_shipments || []).length === 0
                  ? <div style={{ color: T.subtext, fontSize: 13, textAlign: "center", padding: 20 }}>هیچ باری ثبت نشده</div>
                  : (stats?.recent_shipments || []).map((s: any, i: number) => (
                    <div key={i} onClick={() => setTab("shipments")} style={{ padding: "10px 8px", borderBottom: `1px solid ${T.cardBorder}`, cursor: "pointer", borderRadius: 6 }}
                      onMouseEnter={e => (e.currentTarget.style.background = dark ? "rgba(249,115,22,0.05)" : "rgba(249,115,22,0.03)")}
                      onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ padding: "3px 8px", borderRadius: 6, background: `${STATUS_COLORS[s.status]}20`, color: STATUS_COLORS[s.status], fontSize: 10, fontWeight: 700 }}>
                            {STATUS_LABELS[s.status]}
                          </span>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>
                              {s.serial}
                              {/* 🆕 invoice_number */}
                              {s.invoice_number && (
                                <span style={{ marginRight: 6, fontSize: 10, color: "#8b5cf6", fontWeight: 600 }}>
                                  🧾 {s.invoice_number}
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 10, color: T.subtext }}>{s.customs_office_name} • {s.bar_number || "-"}</div>
                          </div>
                        </div>
                        <div style={{ textAlign: "left" }}>
                          <div style={{ fontSize: 11, color: T.subtext }}>{s.entry_date}</div>
                          <div style={{ fontSize: 10, color: T.subtext }}>{s.items_count} جنس</div>
                        </div>
                      </div>
                      {/* 🆕 توضیحات */}
                      {s.notes && (
                        <div style={{ marginTop: 6, fontSize: 11, color: T.subtext, fontStyle: "italic", paddingRight: 4 }}>
                          📝 {s.notes}
                        </div>
                      )}
                    </div>
                  ))
                }
              </div>

              {/* هشدارها */}
              <div style={{ borderRadius: 16, padding: 20, background: dark ? "rgba(239,68,68,0.05)" : "#fef2f2", border: "1px solid rgba(239,68,68,0.2)" }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#dc2626", marginBottom: 16 }}>⚠️ بارهای ماندگار در گمرک (بیش از 7 روز)</div>
                {(stats?.alerts || []).length === 0
                  ? <div style={{ color: T.subtext, fontSize: 13, textAlign: "center", padding: 20 }}>هیچ هشداری وجود ندارد ✓</div>
                  : (stats?.alerts || []).map((a: any, i: number) => (
                    <div key={i} onClick={() => setTab("shipments")} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 8px", borderBottom: "1px solid rgba(239,68,68,0.1)", cursor: "pointer" }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{a.serial}</div>
                        <div style={{ fontSize: 10, color: T.subtext }}>{a.customs_office_name} • ورود: {a.entry_date}</div>
                      </div>
                      <div style={{ padding: "4px 10px", borderRadius: 8, background: a.days_in_customs > 15 ? "#dc2626" : "#f59e0b", color: "#fff", fontSize: 12, fontWeight: 800 }}>
                        {a.days_in_customs} روز
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        )
      )}

      {tab === "shipments" && <ShipmentsList />}
      {tab === "shortage"  && <ShortageReport />}
      {tab === "offices"   && <OfficesManager />}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}